import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import {
  Plus,
  Trash2,
  Loader2,
  GripVertical,
  ListChecks,
} from 'lucide-react'
import {
  useSubtareas,
  useCrearSubtarea,
  useToggleSubtarea,
  useEliminarSubtarea,
} from '@/core/hooks/useTareas'
import type { TareaSubtarea } from '@/core/types/tareas'
import { cn } from '@/lib/utils'
import { handleMutationError } from '@/lib/permisos-guard'

interface SubtareasChecklistProps {
  tareaId: string
  className?: string
  /**
   * ORR-386: mismo gate que `canChangeEstado` de `TareaDetalleDialog` — la subtarea hereda el
   * permiso de la tarea padre (`puedeTocarTarea`), no tiene dueño propio. Default `false`: un
   * caller que se olvide de pasarlo bloquea, no habilita (mismo criterio que ORR-381).
   */
  canEdit?: boolean
}

export function SubtareasChecklist({ tareaId, className, canEdit = false }: SubtareasChecklistProps) {
  const [nuevaSubtarea, setNuevaSubtarea] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  // Queries y mutations
  const { data: subtareas = [], isLoading } = useSubtareas(tareaId)
  const crearSubtarea = useCrearSubtarea()
  const toggleSubtarea = useToggleSubtarea()
  const eliminarSubtarea = useEliminarSubtarea()

  // Calcular progreso
  const completadas = subtareas.filter((s) => s.completada).length
  const total = subtareas.length
  const progreso = total > 0 ? Math.round((completadas / total) * 100) : 0

  const handleAgregar = useCallback(async () => {
    const titulo = nuevaSubtarea.trim()
    if (!titulo) return

    try {
      await crearSubtarea.mutateAsync({
        tareaId,
        datos: { titulo },
      })
      setNuevaSubtarea('')
      setIsAdding(false)
    } catch (error) {
      handleMutationError(error, 'Error al agregar subtarea')
    }
  }, [nuevaSubtarea, tareaId, crearSubtarea])

  const handleToggle = useCallback(
    async (subtarea: TareaSubtarea) => {
      try {
        await toggleSubtarea.mutateAsync({
          id: subtarea.id,
          tareaId,
          completada: !subtarea.completada,
        })
      } catch (error) {
        handleMutationError(error, 'Error al actualizar subtarea')
      }
    },
    [tareaId, toggleSubtarea]
  )

  const handleEliminar = useCallback(
    async (id: string) => {
      try {
        await eliminarSubtarea.mutateAsync({ id, tareaId })
      } catch (error) {
        handleMutationError(error, 'Error al eliminar subtarea')
      }
    },
    [tareaId, eliminarSubtarea]
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleAgregar()
    } else if (e.key === 'Escape') {
      setNuevaSubtarea('')
      setIsAdding(false)
    }
  }

  if (isLoading) {
    return (
      <div className={cn('flex justify-center py-4', className)}>
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Header con progreso */}
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="h-4 w-4" />
        <h4 className="text-sm font-medium flex-1">
          Checklist {total > 0 && `(${completadas}/${total})`}
        </h4>
        {total > 0 && (
          <span className="text-xs text-muted-foreground">{progreso}%</span>
        )}
      </div>

      {/* Barra de progreso */}
      {total > 0 && (
        <Progress value={progreso} className="h-1.5 mb-4" />
      )}

      {/* Lista de subtareas */}
      <div className="space-y-2 mb-3">
        {subtareas.map((subtarea) => (
          <div
            key={subtarea.id}
            className={cn(
              'group flex items-center gap-2 p-2 rounded-lg border bg-background hover:bg-muted/50 transition-colors',
              subtarea.completada && 'bg-muted/30'
            )}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground/50 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity" />

            <Checkbox
              checked={subtarea.completada}
              onCheckedChange={() => handleToggle(subtarea)}
              disabled={!canEdit}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />

            <span
              className={cn(
                'flex-1 text-sm',
                subtarea.completada && 'line-through text-muted-foreground'
              )}
            >
              {subtarea.titulo}
            </span>

            {/* ORR-386: sin permiso, ni se ofrece el control — el UPDATE de por sí ya lo
                rechazaría el guard del hook. */}
            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                onClick={() => handleEliminar(subtarea.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}

        {subtareas.length === 0 && !isAdding && (
          <p className="text-sm text-muted-foreground text-center py-2">
            No hay items en el checklist
          </p>
        )}
      </div>

      {/* Input para agregar — ORR-386: crear subtarea también pasa por el mismo permiso */}
      {!canEdit ? null : isAdding ? (
        <div className="flex items-center gap-2">
          <Input
            placeholder="Nueva subtarea..."
            value={nuevaSubtarea}
            onChange={(e) => setNuevaSubtarea(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            className="flex-1 h-8 ml-0.5"
          />
          <Button
            size="sm"
            className="h-8 px-3"
            onClick={handleAgregar}
            disabled={!nuevaSubtarea.trim() || crearSubtarea.isPending}
          >
            {crearSubtarea.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => {
              setNuevaSubtarea('')
              setIsAdding(false)
            }}
          >
            Cancelar
          </Button>
        </div>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-muted-foreground"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar item
        </Button>
      )}
    </div>
  )
}
